import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Debounce-autosaves the résumé editor: serializes every [data-resume-field]
// input into a nested object, writes it to the hidden `resume[data]` field,
// and requestSubmit()s the form (PATCH). The server replies with a Turbo
// Stream that re-renders #resume_preview.
export default class extends Controller {
  static targets = ["form", "data", "status", "theme"]
  static values  = { delay: { type: Number, default: 500 } }

  connect() {
    this.timeout = null
    this.sortables = []
    const railSections = this.element.querySelector("[data-resume-rail-sections]")
    if (railSections) {
      this.sortables.push(Sortable.create(railSections, {
        handle: "[data-resume-handle]", animation: 150, draggable: "[data-resume-module]",
        onEnd: () => this.scheduleSave()
      }))
    }
    this.element.querySelectorAll("[data-resume-items]").forEach((list) => {
      this.sortables.push(Sortable.create(list, {
        handle: "[data-resume-handle]", animation: 150, draggable: "[data-resume-item]",
        onEnd: () => this.scheduleSave()
      }))
    })
    const basicList = this.element.querySelector("[data-resume-basic-fields]")
    if (basicList) {
      this.sortables.push(Sortable.create(basicList, { handle: "[data-resume-handle]", animation: 150, draggable: "[data-resume-basic-field]", onEnd: () => this.scheduleSave() }))
    }
    const cfl = this.element.querySelector("[data-resume-custom-fields]")
    if (cfl) this.sortables.push(Sortable.create(cfl, { handle: "[data-resume-handle]", animation: 150, draggable: "[data-resume-custom-field]", onEnd: () => this.scheduleSave() }))
    this.activate("basic")
  }

  disconnect() {
    clearTimeout(this.timeout)
    ;(this.sortables || []).forEach((s) => s.destroy())
  }

  scheduleSave() {
    this.setStatus("Saving…")
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.save(), this.delayValue)
  }

  save() {
    this.serialize()
    this.formTarget.requestSubmit()
  }

  serialize() {
    const obj = { basic: {}, sections: [] }

    this.element.querySelectorAll("[data-resume-field]").forEach((el) => {
      const path = el.dataset.resumeField.split(".")
      const val = el.isContentEditable ? el.innerHTML : el.value
      let node = obj
      path.forEach((key, i) => {
        if (i === path.length - 1) { node[key] = val }
        else { node[key] = node[key] || {}; node = node[key] }
      })
    })

    const basicRows = this.element.querySelectorAll("[data-resume-basic-field]")
    if (basicRows.length) {
      obj.basic.fieldOrder = []
      obj.basic.hidden = []
      basicRows.forEach((row) => {
        const k = row.dataset.resumeBasicField
        obj.basic.fieldOrder.push(k)
        if (row.dataset.resumeFieldVisible === "false") obj.basic.hidden.push(k)
      })
    }

    const cfRows = this.element.querySelectorAll("[data-resume-custom-field]")
    obj.basic.customFields = []
    cfRows.forEach((row) => {
      obj.basic.customFields.push({
        label: row.querySelector('[data-resume-cf="label"]') ? row.querySelector('[data-resume-cf="label"]').value : "",
        value: row.querySelector('[data-resume-cf="value"]') ? row.querySelector('[data-resume-cf="value"]').value : "",
        showLabel: row.dataset.resumeCfShowlabel !== "false",
        visible: row.dataset.resumeFieldVisible !== "false"
      })
    })

    // sections: order + visibility + title from the rail; content from the center editor
    this.element.querySelectorAll("[data-resume-rail-sections] [data-resume-module]").forEach((row) => {
      const key = row.dataset.resumeModule
      const section = { key, title: row.dataset.resumeModuleTitle || "", visible: row.dataset.resumeModuleVisible !== "false" }
      const editor = this.element.querySelector('[data-resume-section="' + key + '"]')
      if (editor) {
        const content = editor.querySelector("[data-resume-section-content]")
        if (content) {
          section.content = content.value
        } else {
          section.items = []
          editor.querySelectorAll("[data-resume-item]").forEach((itemEl) => {
            const item = { visible: itemEl.dataset.resumeItemVisible !== "false" }
            itemEl.querySelectorAll("[data-resume-item-field]").forEach((fEl) => {
              item[fEl.dataset.resumeItemField] = fEl.isContentEditable ? fEl.innerHTML : fEl.value
            })
            section.items.push(item)
          })
        }
      }
      obj.sections.push(section)
    })

    const sumRow = this.element.querySelector('[data-resume-module="summary"]')
    obj.summaryVisible = !sumRow || sumRow.dataset.resumeModuleVisible !== "false"

    this.dataTarget.value = JSON.stringify(obj)

    if (this.hasThemeTarget) {
      const theme = {}
      this.element.querySelectorAll("[data-resume-theme]").forEach((el) => { theme[el.dataset.resumeTheme] = el.value })
      this.themeTarget.value = JSON.stringify(theme)
    }
  }

  addItem(event) {
    const sectionEl = event.currentTarget.closest("[data-resume-section]")
    const tpl = sectionEl.querySelector("template[data-resume-item-template]")
    const list = sectionEl.querySelector("[data-resume-items]")
    if (!tpl || !list) return
    list.appendChild(tpl.content.cloneNode(true))
    this.scheduleSave()
  }

  removeItem(event) {
    const itemEl = event.currentTarget.closest("[data-resume-item]")
    if (itemEl) { itemEl.remove(); this.scheduleSave() }
  }

  // Repaint a visibility toggle so its label, glyph and accessible name match
  // the state it just moved to. `nowVisible` is the state AFTER the flip.
  //
  // Both call sites server-render these three from the same state, and nothing
  // else keys off data-resume-item-visible / data-resume-field-visible — a
  // 40% opacity drop used to be the entire signal, so a hidden entry still
  // read "Hide" with the eye-open glyph and clicking it again showed the entry.
  //
  // The entry button (_item_fields.html.erb) is `<span icon>Hide`: its label is
  // a bare text node after the icon span, so it is looked up as such rather
  // than assumed to be lastChild. The basic-field button (edit.html.erb) is
  // icon-only, so its ligature IS its text and aria-label carries the name.
  syncVisibilityControl(btn, nowVisible) {
    const icon = btn.classList.contains("material-symbols-outlined")
      ? btn
      : btn.querySelector(".material-symbols-outlined")
    if (icon) icon.textContent = nowVisible ? "visibility" : "visibility_off"
    const name = nowVisible ? "Hide" : "Show"
    const text = Array.from(btn.childNodes).find((n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim())
    if (text && icon !== btn) text.textContent = name
    btn.setAttribute("aria-label", name)
    btn.classList.toggle("opacity-40", !nowVisible)
  }

  toggleItem(event) {
    const itemEl = event.currentTarget.closest("[data-resume-item]")
    const on = itemEl.dataset.resumeItemVisible !== "false"
    itemEl.dataset.resumeItemVisible = on ? "false" : "true"
    this.syncVisibilityControl(event.currentTarget, !on)
    this.scheduleSave()
  }

  // Expands/collapses a repeatable-section entry card (.rbf-item). Distinct
  // from toggleItem above, which flips the entry's *visible-on-resume* flag —
  // this only toggles the collapsed/expanded UI state, no save needed. The
  // button (.rbf-ihead-btn) covers only the identity/score/caret region, so
  // .rbf-ibody is looked up from the item rather than assumed to be the
  // button's nextElementSibling.
  toggleItemOpen(event) {
    const itemEl = event.currentTarget.closest("[data-resume-item]")
    if (!itemEl) return
    const willOpen = itemEl.classList.toggle("is-open")
    const body = itemEl.querySelector(".rbf-ibody")
    if (body) body.hidden = !willOpen
    const caret = event.currentTarget.querySelector(".rbf-icaret")
    if (caret) caret.textContent = willOpen ? "expand_less" : "expand_more"
    event.currentTarget.setAttribute("aria-expanded", willOpen)
  }

  toggleBasicField(event) {
    const row = event.currentTarget.closest("[data-resume-basic-field]")
    const on = row.dataset.resumeFieldVisible !== "false"
    row.dataset.resumeFieldVisible = on ? "false" : "true"
    this.syncVisibilityControl(event.currentTarget, !on)
    this.scheduleSave()
  }

  removeBasicField(event) {
    const row = event.currentTarget.closest("[data-resume-basic-field]")
    if (row) { row.remove(); this.scheduleSave() }
  }

  addCustomField() {
    const tpl = this.element.querySelector("template[data-resume-custom-field-template]")
    const list = this.element.querySelector("[data-resume-custom-fields]")
    if (tpl && list) { list.appendChild(tpl.content.cloneNode(true)); this.scheduleSave() }
  }

  removeCustomField(event) {
    const row = event.currentTarget.closest("[data-resume-custom-field]")
    if (row) { row.remove(); this.scheduleSave() }
  }

  toggleCustomLabel(event) {
    const row = event.currentTarget.closest("[data-resume-custom-field]")
    const on = row.dataset.resumeCfShowlabel !== "false"
    row.dataset.resumeCfShowlabel = on ? "false" : "true"
    event.currentTarget.classList.toggle("opacity-40", on)
    this.scheduleSave()
  }

  toggleGithub(event) {
    const input = this.element.querySelector('[data-gh-show]')
    const on = input.value === "true"
    input.value = on ? "false" : "true"
    // .rbf-tog.is-on (app.tailwind.css) carries both the track color and,
    // via a descendant selector, the knob's translated position — one class
    // toggle instead of separately juggling the knob's own classList.
    event.currentTarget.classList.toggle("is-on", !on)
    this.scheduleSave()
  }

  renameModule(event) {
    const ed = event.currentTarget.closest("[data-resume-module-editor]")
    if (!ed) return
    const key = ed.dataset.resumeModuleEditor
    // Update EVERY matching tab/rail element. serialize() reads section
    // titles from [data-resume-rail-sections] [data-resume-module], so
    // updating only the first match (the visible studio_tabs row) leaves
    // the rail row stale and autosave posts the OLD title, reverting the
    // custom section name on the next render.
    this.element
      .querySelectorAll('[data-resume-module="' + key + '"]')
      .forEach((row) => {
        row.dataset.resumeModuleTitle = event.currentTarget.value
        const label = row.querySelector("[data-resume-module-label]")
        if (label) label.textContent = event.currentTarget.value
      })
    this.scheduleSave()
  }

  selectModule(event) {
    const key = (event.currentTarget.dataset.resumeModule) || event.currentTarget.closest("[data-resume-module]")?.dataset.resumeModule
    if (key) this.activate(key)
  }

  activate(key) {
    this.element.querySelectorAll("[data-resume-module-editor]").forEach((ed) => {
      ed.classList.toggle("hidden", ed.dataset.resumeModuleEditor !== key)
    })
    this.element.querySelectorAll("[data-resume-module]").forEach((row) => {
      row.classList.toggle("rb-rail-active", row.dataset.resumeModule === key)
    })
  }

  toggleModule(event) {
    event.stopPropagation()
    const row = event.currentTarget.closest("[data-resume-module]")
    const on = row.dataset.resumeModuleVisible !== "false"
    row.dataset.resumeModuleVisible = on ? "false" : "true"
    event.currentTarget.classList.toggle("opacity-40", on)
    this.scheduleSave()
  }

  deleteModule(event) {
    event.stopPropagation()
    const row = event.currentTarget.closest("[data-resume-module]")
    const key = row.dataset.resumeModule
    const editor = this.element.querySelector('[data-resume-module-editor="' + key + '"]')
    row.remove()
    if (editor) editor.remove()
    this.activate("basic")
    this.scheduleSave()
  }

  pickSwatch(event) {
    const color = event.currentTarget.dataset.color
    const input = this.element.querySelector('[data-resume-theme="accent"]')
    if (!input || !color) return
    input.value = color
    this.element.querySelectorAll("[data-resume-swatch]").forEach((s) => s.classList.toggle("rb-sw-on", s.dataset.color === color))
    this.scheduleSave()
  }

  pickAlign(event) {
    const val = event.currentTarget.dataset.align
    const input = this.element.querySelector('[data-resume-field="basic.align"]')
    if (input) input.value = val
    this.element.querySelectorAll("[data-resume-align]").forEach((b) => {
      b.classList.toggle("is-on", b.dataset.align === val)
    })
    this.scheduleSave()
  }

  saved() { this.setStatus("Saved") }

  setStatus(text) { if (this.hasStatusTarget) this.statusTarget.textContent = text }
}
